import { useMemo } from 'react';
import { useLocation } from 'react-router';

import {
  projectsRoutesConfig,
  projectsV2RoutesConfig,
} from 'domains/projects/routes';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useJWTs } from 'domains/jwtToken/hooks/useJWTs';
import { useAppSelector } from 'store/useAppSelector';

import { selectDraftProject } from '../store';

const { useParams } = projectsRoutesConfig.project;
const { path: newProjectPath } = projectsV2RoutesConfig.newProject;

export interface UseSelectedProjectParams {
  nestedEndpointToken?: string;
}

export const useSelectedProject = ({
  nestedEndpointToken,
}: UseSelectedProjectParams = {}) => {
  const { legacyProjectId, projectIndex } = useParams();

  const { group } = useAccount();

  const { pathname } = useLocation();

  const draftProject = useAppSelector(state =>
    selectDraftProject(state, { group }),
  );
  const isNewProjectFlow = pathname === newProjectPath;

  const {
    jwts: projects,
    state: { isError, isSuccess },
  } = useJWTs({ group, skipFetching: isNewProjectFlow });

  const project = useMemo(() => {
    if (isNewProjectFlow && draftProject) {
      return draftProject;
    }

    // Use nestedEndpointToken if provided, otherwise fall back to the legacy
    // projectId param (the project API key) from old links
    const targetToken = nestedEndpointToken || legacyProjectId;

    if (nestedEndpointToken || projectIndex === undefined) {
      if (targetToken) {
        return projects.find(
          item =>
            item.userEndpointToken.toLowerCase() === targetToken.toLowerCase(),
        );
      }

      return undefined;
    }

    return projects.find(item => item.index === projectIndex);
  }, [
    draftProject,
    isNewProjectFlow,
    projects,
    projectIndex,
    legacyProjectId,
    nestedEndpointToken,
  ]);

  const isLoaded = isError || isSuccess || isNewProjectFlow;

  return { isLoaded, project, isNewProjectFlow };
};
